// The mascot's place on the reveal and the podium: a reserved, fixed-size box so a character chosen later can take it
// without anything around it moving. Until then the Pulse stands in, in the mood of the moment, and nothing else.
//
//   right   spike (a burst and sparks)       wrong   jolt (the defib shock)
//   partial steady (neither a win nor a miss) none    flat (the soft flatline)
//   podium  crown
//
// The box is `size` × `size` px (square: the most neutral frame for whatever character arrives). The Pulse paints
// across its width and may spark above it, so the box never clips. Decorative unless `label` is given.
//
// Under the medsnapp look (3 Nov 2026) the box holds MedSnapp's cast instead (CastMascot, same box): the organ character
// of the question's category (the `category` prop, else the current question's from the game store), and Beat, crowned,
// on the podium. Every other look keeps the Pulse exactly as before.
//
// The act (4 Nov 2026): picked at random for the moment, never the same act twice in a row in the slot, seeded so a
// reload shows the same one: on the phone by battle code, player and question; on the stage (callers pass `seed` and
// `index`) by battle code and question, one per question.
import { Pulse, type PulseMood } from './Pulse';
import { useEffect } from 'react';
import { CastMascot, preloadCast } from './CastMascot';
import { DEFAULT_CAST, mascotFor } from './cast';
import { pickAct } from './acts';
import { useLook } from '@/look';
import { useGame } from '@/store/game';

export type MascotMoment = 'right' | 'wrong' | 'partial' | 'none' | 'podium';

export const MASCOT_MOOD: Readonly<Record<MascotMoment, PulseMood>> = { right: 'spike', wrong: 'jolt', partial: 'steady', none: 'flat', podium: 'crown' };
const TONE: Readonly<Record<MascotMoment, string>> = { right: 'text-correct', wrong: 'text-wrong', partial: 'text-cart', none: 'text-ink-3', podium: 'text-cart' };

/** The cast member the medsnapp look shows for a moment: Beat on the podium, else the category's organ character. */
export const castFor = (moment: MascotMoment, category?: string): string => (moment === 'podium' ? DEFAULT_CAST : mascotFor(category));

/** Under the medsnapp look, fetches the reveal's character while the question is open, so its act starts on time. */
export function usePreloadMascot(category?: string) {
  const { look } = useLook();
  const name = look === 'medsnapp' ? mascotFor(category) : undefined;
  useEffect(() => { if (name) preloadCast(name); }, [name]);
}

/** The act a slot plays: the stage passes its battle code as the seed; a phone seeds by battle code and player. */
export const actFor = (moment: MascotMoment, seed: string, index: number) => pickAct(moment, seed, index);

export function MascotSlot({ moment, size = 64, className = '', label, category, seed, index }: {
  moment: MascotMoment; size?: number; className?: string; label?: string; category?: string;
  /** the stage's battle code; left out, the phone's battle code and player */
  seed?: string;
  /** the question; left out, the current one */
  index?: number;
}) {
  const { look } = useLook();
  const current = useGame((s) => s.snapshot?.question?.category);
  const code = useGame((s) => s.snapshot?.code ?? s.session?.code);
  const at = useGame((s) => s.snapshot?.index);
  const player = useGame((s) => s.session?.playerId);
  if (look === 'medsnapp') {
    const act = actFor(moment, seed ?? `${code ?? ''}:${player ?? ''}`, index ?? at ?? 0);
    return (
      <span data-part="mascot-slot" data-moment={moment} data-cast-slot="" {...(label ? {} : { 'aria-hidden': true as const })}
        className={`relative inline-flex shrink-0 items-center justify-center ${className}`}
        style={{ width: size, height: size, overflow: 'visible' }}>
        <CastMascot name={castFor(moment, category ?? current)} mood={moment} size={size} label={label} act={act} />
      </span>
    );
  }
  const a11y = label ? {} : { 'aria-hidden': true as const };
  return (
    <span data-part="mascot-slot" data-moment={moment} {...a11y}
      className={`relative inline-flex shrink-0 items-center justify-center ${TONE[moment]} ${className}`}
      style={{ width: size, height: size, overflow: 'visible' }}>
      <Pulse mood={MASCOT_MOOD[moment]} width={size} height={Math.round(size * 0.42)} glow={moment === 'right' || moment === 'podium'} label={label} />
    </span>
  );
}
